const EVENTS = [
    'diagnostics-start', 'voice-phase', 'microphone-phase', 'microphone-error',
    'transcribe-start', 'transcribe-response', 'transcribe-error',
    'chat-state', 'xr-start', 'xr-end', 'xr-visibility', 'xr-unmount',
    'page-visibility', 'page-hide', 'browser-error', 'browser-rejection',
    'page-unload', 'page-show', 'next-resource-error',
    'stream-open', 'stream-error', 'stream-invalid', 'stream-bridge-error', 'stream-close',
] as const;
const VALUES = [
    'idle', 'requesting', 'ready', 'recording', 'stopping', 'playing', 'transcribing', 'error',
    'visible', 'hidden', 'visible-blurred', 'unknown',
    'Error', 'TypeError', 'RangeError', 'AbortError', 'TimeoutError', 'NotAllowedError',
    'NotFoundError', 'NotReadableError', 'InvalidStateError', 'SecurityError',
    'navigate', 'reload', 'back_forward', 'prerender',
] as const;
type Event = typeof EVENTS[number];
type Value = typeof VALUES[number];
type Metadata = { phase?: Value; errorType?: Value; status?: number; bytes?: number;
    elapsedMs?: number; aborted?: boolean; sending?: boolean; failed?: boolean; persisted?: boolean;
    readyState?: number; requestId?: string };
export type VoiceDiagnostic = Metadata & { event: Event; pageId: string; sequence: number; at: number };
const ID = /^[a-f0-9-]{36}$/;
const KEYS = new Set(['event', 'pageId', 'sequence', 'at', 'phase', 'errorType', 'status', 'bytes',
    'elapsedMs', 'aborted', 'sending', 'failed', 'persisted', 'readyState', 'requestId']);

export function isVoiceDiagnostic(value: unknown): value is VoiceDiagnostic {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const record = value as Record<string, unknown>;
    return Object.keys(record).every(key => KEYS.has(key))
        && EVENTS.some(event => event === record.event)
        && typeof record.pageId === 'string' && ID.test(record.pageId)
        && ['sequence', 'at'].every(key => typeof record[key] === 'number'
            && Number.isSafeInteger(record[key]) && record[key] >= 0)
        && ['phase', 'errorType'].every(key => record[key] === undefined || VALUES.some(item => item === record[key]))
        && ['status', 'bytes', 'elapsedMs'].every(key => record[key] === undefined
            || typeof record[key] === 'number' && Number.isFinite(record[key]) && record[key] >= 0 && record[key] <= 1e12)
        && ['aborted', 'sending', 'failed', 'persisted'].every(key => record[key] === undefined || typeof record[key] === 'boolean')
        && (record.readyState === undefined || record.readyState === 0 || record.readyState === 1 || record.readyState === 2)
        && (record.requestId === undefined || typeof record.requestId === 'string' && ID.test(record.requestId));
}

export function diagnosticErrorType(error: unknown): Value {
    const name = error instanceof Error ? error.name : 'unknown';
    return VALUES.find(value => value === name) ?? 'unknown';
}

let enabled = false;
let pageId: string;
let sequence = 0;
let count = 0;
let interval = 0;
let streamSnapshot = { phase: 'not connected', updatedAt: '' };
const streamListeners = new Set<() => void>();
export const ahpStreamDiagnostics = {
    getSnapshot: () => streamSnapshot,
    subscribe(listener: () => void) { streamListeners.add(listener); return () => { streamListeners.delete(listener); }; },
    update(phase: 'connecting' | 'connected' | 'invalid frame' | 'reconnecting' | 'bridge interrupted' | 'closed') {
        streamSnapshot = { phase, updatedAt: new Date().toISOString() };
        streamListeners.forEach(listener => listener());
    },
};

export function enableVoiceDiagnostics() {
    enabled = true;
    pageId ??= crypto.randomUUID();
    voiceDiagnostic('diagnostics-start');
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    const phase = VALUES.find(value => value === navigation?.type) ?? 'unknown';
    voiceDiagnostic('page-show', { phase });
    return () => { enabled = false; };
}

/** Only allowlisted technical metadata leaves the browser, including during page teardown. */
export function voiceDiagnostic(event: Event, metadata: Metadata = {}) {
    if (!enabled) return;
    const now = Date.now();
    if (now - interval >= 60_000) { interval = now; count = 0; }
    if (count++ >= 100) return;
    const entry: VoiceDiagnostic = { ...metadata, event, pageId, sequence: ++sequence, at: now };
    if (!isVoiceDiagnostic(entry)) { console.error('[voice-diagnostics] Invalid diagnostic metadata.'); return; }
    void fetch('/api/agent/voice-diagnostics', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry), keepalive: true,
    }).then(response => {
        if (!response.ok) console.error('[voice-diagnostics] Upload rejected:', response.status);
    }).catch(() => console.error('[voice-diagnostics] Upload failed.'));
}
